/**
 * Dari (fa-AF) — the default locale and the source of truth for keys.
 *
 * Every string the panel shows lives here first; `en.ts` and `ps.ts` are
 * typed as `Record<StringKey, string>`, so a key missing from either one is
 * a compile error. That mirrors the Android rule: a string exists in all
 * three locales in the same commit, never one ahead of the others.
 *
 * Keys are module-prefixed (`nav_`, `login_`, `dashboard_`, `cp_`, …) so two
 * modules cannot collide. `{name}` placeholders are filled by `t(key, {name})`.
 */
export const fa = {
  // common — shared across modules
  common_menu: "منو",
  common_close: "بستن",
  common_cancel: "انصراف",
  common_language: "زبان",

  // app shell
  app_name: "تالار",
  app_initial: "ت",

  // nav (DashboardLayout)
  nav_group_ops: "عملیات",
  nav_group_finance: "مالی و اداری",
  nav_group_market: "بازار",
  nav_group_platform: "پلتفرم",
  nav_dashboard: "داشبورد",
  nav_calendar: "تقویم رزرو",
  nav_bookings: "رزروها",
  nav_contracts: "قراردادها",
  nav_crm: "مشتریان",
  nav_finance: "مالی",
  nav_payouts: "تسویه‌ها",
  nav_hr: "کارمندان",
  nav_halls: "تالارها",
  nav_reviews: "نظرات",
  nav_admin: "مدیریت پلتفرم",
  nav_change_password: "تغییر رمز",
  nav_logout: "خروج",
  layout_panel_fallback: "پنل مدیریت",
  layout_today: "امروز: {date}",

  // login
  login_subtitle: "پنل مدیریت تالارهای عروسی",
  login_email: "ایمیل",
  login_password: "رمز عبور",
  login_submit: "ورود",
  login_submitting: "در حال ورود…",
  login_failed: "ورود ناموفق بود",

  // onboarding
  onboarding_welcome: "خوش آمدید",
  onboarding_intro:
    "برای شروع، نام تجارت یا تالارتان را ثبت کنید. بعد از این قدم، پنل کامل (رزروها، تقویم، مالی، کارمندان، مشتریان…) برای شما باز می‌شود.",
  onboarding_name_label: "نام سازمان / تالار",
  onboarding_name_placeholder: "مثلاً: شرکت تالارهای آریانا",
  onboarding_submit: "ساخت سازمان و ورود به پنل",
  onboarding_submitting: "در حال ساخت…",
  onboarding_error: "خطا در ساخت سازمان",

  // route guard (RequireRole)
  guard_no_access_title: "دسترسی ندارید",
  guard_no_access_body:
    "این بخش برای نقش «{roles}» است. برای دسترسی با مالک تالار تماس بگیرید.",

  // dashboard
  dashboard_title: "داشبورد",
  dashboard_kpi_confirmed: "رزروهای تأیید شده",
  dashboard_kpi_pending_requests: "درخواست‌های در انتظار",
  dashboard_kpi_pending_requests_hint: "نیازمند تأیید شما",
  dashboard_kpi_pending_deposits: "در انتظار بیعانه",
  dashboard_kpi_upcoming: "محافل ۷ روز آینده",
  dashboard_insights_title: "هوش تالار",

  // change password (ChangePasswordDialog)
  cp_title: "تغییر رمز عبور",
  cp_intro: "برای امنیت، اول رمز فعلی را بنویسید.",
  cp_current_label: "رمز فعلی",
  cp_new_label: "رمز جدید",
  cp_repeat_label: "تکرار رمز جدید",
  cp_min_helper: "حداقل {n} حرف",
  cp_submit: "تغییر رمز",
  cp_submitting: "در حال تغییر…",
  cp_success: "رمز عبور عوض شد. دفعهٔ بعد با رمز جدید وارد شوید.",
  cp_min_length: "رمز جدید باید حداقل {n} حرف باشد",
  cp_mismatch: "رمز جدید و تکرار آن یکسان نیستند",
  cp_same_as_current: "رمز جدید با رمز فعلی تفاوتی ندارد",
  cp_err_no_email: "این حساب با ایمیل وارد نشده — تغییر رمز از اینجا ممکن نیست",
  cp_err_wrong_password: "رمز فعلی درست نیست",
  cp_err_weak: "رمز جدید بسیار ساده است — حرف و رقم را با هم بیامیزید",
  cp_err_too_many: "تلاش زیاد شد — چند دقیقه صبر کنید و باز امتحان کنید",
  cp_err_recent_login: "نشست شما کهنه شده — یک بار خارج و دوباره وارد شوید",
  cp_err_network: "اتصال برقرار نشد — انترنت را بررسی کنید",
  cp_err_generic: "تغییر رمز ناکام شد — دوباره امتحان کنید",

  // shared — actions, columns, enums
  common_error: "خطا",
  common_send: "ارسال",
  common_empty: "موردی نیست",
  common_status: "وضعیت",
  shift_morning: "صبح",
  shift_evening: "شب",
  shift_full_day: "تمام روز",
  booking_status_pending_request: "در انتظار تأیید",
  booking_status_pending_deposit: "در انتظار بیعانه",
  booking_status_confirmed: "تأیید شده",
  booking_status_completed: "برگزار شده",
  booking_status_cancelled: "لغو شده",

  // bookings
  bookings_title: "رزروها",
  booking_tab_requests: "درخواست‌ها",
  booking_tab_pending_deposit: "در انتظار بیعانه",
  booking_tab_confirmed: "تأیید شده",
  booking_tab_completed: "برگزار شده",
  bookings_col_date: "تاریخ",
  bookings_col_shift: "وقت",
  bookings_col_guests: "مهمانان",
  bookings_col_total: "مجموع",
  bookings_col_paid: "پرداخت شده",
  bookings_col_balance: "باقی‌مانده",
  bookings_confirm: "تأیید",
  bookings_contract: "قرارداد",
  bookings_issue_contract: "صدور قرارداد",
  bookings_toast_confirmed: "درخواست تأیید شد — مشتری برای پرداخت بیعانه خبر شد",
  bookings_toast_contract_issued: "قرارداد صادر شد — مشتری برای امضا خبر شد",
  bookings_toast_receipt: "رسید {no} ثبت شد — در صفحه مالی تأییدش کنید تا به دفاتر برود",

  // receipt dialog
  receipt_title: "ثبت رسید دریافتی",
  receipt_submit: "ثبت رسید",
  receipt_balance: "باقی‌مانده این رزرو: {amount}",
  receipt_amount_label: "مبلغ (افغانی)",
  receipt_method_label: "طریقه دریافت",
  receipt_method_cash: "نقد (صندوق)",
  receipt_method_bank: "حواله بانکی",
  receipt_note_label: "یادداشت (اختیاری)",
  receipt_failed: "ثبت رسید ناکام شد",

  // calendar
  calendar_day_tooltip: "{count} مهمان · {status}",

  // chats
  chats_title: "گفتگو با مشتریان",
  chats_empty: "هنوز گفتگویی نیست",
  chats_reply_placeholder: "پاسخ بنویسید…",

  // reviews
  reviews_title: "نظرات مشتریان",
  reviews_your_reply: "پاسخ شما",
  reviews_reply_placeholder: "پاسخ خود را بنویسید…",
  reviews_empty: "هنوز نظر منتشرشده‌ای ثبت نشده است",

  // contracts (page)
  contracts_title: "قراردادها",
  contracts_subtitle:
    "قراردادهای دیجیتال مهر‌شده — صدور از صفحهٔ رزروها، امضا توسط مشتری یا حضوری در تالار",
  contracts_all: "همه",
  contracts_awaiting_signature: "در انتظار امضا",
  contracts_signed: "امضا شده",
  contracts_status_signed: "امضا شده ✓",
  contracts_stat_total: "مجموع قراردادها",
  contracts_stat_last_n: "{n} مورد آخر",
  contracts_hint_unsigned: "مشتری هنوز امضا نکرده",
  contracts_col_issued: "تاریخ صدور",
  contracts_col_signed_date: "تاریخ امضا",
  contracts_col_template: "قالب",
  contracts_col_seal: "مهر دیجیتال",
  contracts_template_version: "نسخهٔ {v}",
  contracts_view_print: "مشاهده و چاپ",
  contracts_empty_none:
    "هنوز قراردادی صادر نشده — از صفحهٔ رزروها برای یک رزرو تأییدشده قرارداد صادر کنید",
  contracts_empty_filtered: "در این وضعیت موردی نیست",

  // contract dialog
  contract_dialog_title: "قرارداد دیجیتال",
  contract_awaiting_customer: "در انتظار امضای مشتری",
  contract_seal_line: "مهر دیجیتال: {hash}",
  contract_inperson_note:
    "اگر مشتری قرارداد را حضوری در تالار امضا کرده، اینجا ثبت کنید تا نهایی شود.",
  contract_print_pdf: "چاپ / ذخیره PDF",
  contract_signing: "در حال ثبت…",
  contract_sign_inperson: "ثبت امضای حضوری مشتری",
  contract_sign_failed: "ثبت امضا ناموفق بود",

  // finance
  finance_title: "مالی — عاید و مصارف",
  finance_net_profit: "مفاد خالص",
  finance_total_income: "مجموع عاید",
  finance_total_expenses: "مجموع مصارف",
  finance_pending_receipts: "رسیدهای در انتظار تأیید",
  finance_col_receipt: "رسید",
  finance_col_method: "طریقه",
  finance_col_amount: "مبلغ",
  finance_col_note: "یادداشت",
  finance_method_cash: "نقد",
  finance_method_bank: "بانک",
  finance_verify_post: "تأیید و ثبت در دفاتر",
  finance_payouts_link: "تسویه‌های پلتفرم (پرداخت‌های آنلاین) →",
  finance_coa_title: "حساب‌ها (Chart of Accounts)",
  finance_col_code: "کد",
  finance_col_account: "حساب",
  finance_col_type: "نوع",
  finance_col_balance: "بیلانس",

  // payouts
  payouts_title: "تسویه‌های پلتفرم",
  payouts_subtitle:
    "پرداخت‌های آنلاین مشتریان اول به حساب پلتفرم می‌رود؛ پلتفرم پس از کسر کمیشن، خالص آن را به حساب بانکی تالار انتقال می‌دهد",
  payouts_refresh: "بازخوانی",
  payouts_stat_pending: "در انتظار پرداخت به شما",
  payouts_stat_paid: "تا حال پرداخت شده",
  payouts_stat_commission: "کمیشن پلتفرم",
  payouts_stat_commission_hint: "از مجموع همهٔ دوره‌ها",
  payouts_col_period: "دورهٔ تسویه تا",
  payouts_col_count: "تعداد پرداخت",
  payouts_col_gross: "ناخالص",
  payouts_col_commission: "کمیشن",
  payouts_col_net: "خالص پرداختی",
  payouts_col_paid_date: "تاریخ پرداخت",
  payouts_status_paid: "پرداخت شده",
  payouts_status_pending: "در انتظار",
  payouts_commission_cell: "{amount} ({pct}٪)",
  payouts_empty:
    "هنوز تسویه‌ای انجام نشده — تسویه وقتی ساخته می‌شود که پرداخت آنلاین تأییدشدهٔ تسویه‌نشده وجود داشته باشد",
  payouts_loading: "در حال بارگذاری…",
  payouts_footnote:
    "بیعانه‌ها و پرداخت‌های نقدی که در تالار می‌گیرید در این جدول نیستند — آن پول از اول نزد شماست و در صفحهٔ «مالی» ثبت می‌شود.",
  payouts_load_failed: "دریافت تسویه‌ها ناموفق بود",

  // shared (more)
  common_submit: "ثبت",
  common_note_optional: "یادداشت (اختیاری)",

  // crm
  crm_title: "مشتریان بالقوه (سرنخ‌ها)",
  crm_new_lead: "سرنخ جدید",
  crm_stage_new: "جدید",
  crm_stage_contacted: "تماس گرفته",
  crm_stage_visited: "بازدید کرده",
  crm_stage_quoted: "قیمت داده",
  crm_stage_won: "موفق",
  crm_stage_lost: "ناموفق",
  crm_source_walk_in: "حضوری",
  crm_source_phone: "تلفنی",
  crm_source_instagram: "انستاگرام",
  crm_source_referral: "معرفی",
  crm_source_other: "دیگر",
  crm_guest_estimate_chip: "~{count} مهمان",
  crm_event_approx: "حدوداً {date}",
  crm_advance_next: "{label} ←",
  crm_toast_lead_saved: "سرنخ ثبت شد",
  crm_dialog_title: "سرنخ جدید",
  crm_name: "نام",
  crm_phone: "شماره تماس",
  crm_source_label: "منبع",
  crm_guests_label: "تخمین مهمانان (اختیاری)",

  // hr
  hr_title: "کارمندان و معاشات",
  hr_new_employee: "کارمند جدید",
  hr_create_payroll: "ساخت لیست معاش {period}",
  hr_col_name: "نام",
  hr_col_role: "وظیفه",
  hr_col_salary: "معاش ماهانه",
  hr_col_today_attendance: "حاضری امروز",
  hr_check_inout: "ثبت ورود/خروج",
  hr_empty_employees: "هنوز کارمندی ثبت نشده",
  hr_payroll_runs_title: "لیست‌های معاش",
  hr_col_period: "دوره (شمسی)",
  hr_col_employees: "کارمندان",
  hr_col_net_total: "مجموع خالص",
  hr_run_status_posted: "پرداخت شده",
  hr_run_status_draft: "پیش‌نویس",
  hr_post_run: "پرداخت و ثبت در دفاتر",
  hr_empty_runs: "لیست معاشی ساخته نشده",
  hr_dialog_title: "کارمند جدید",
  hr_full_name: "نام کامل",
  hr_salary_label: "معاش ماهانه (افغانی)",
  hr_toast_employee_saved: "کارمند ثبت شد",
  hr_toast_checked_in: "حاضری ورود ثبت شد",
  hr_toast_checked_out: "خروج ثبت شد",
  hr_toast_payroll_created: "لیست معاش {period} ساخته شد — {count} کارمند، مجموع {total}",
  hr_toast_payroll_posted: "معاش پرداخت و به دفاتر ثبت شد (مصرف معاشات / صندوق)",

  // halls (page)
  halls_title: "تالارهای من",
  halls_add: "ثبت تالار",
  hall_status_draft: "پیش‌نویس",
  hall_status_pending_review: "در انتظار بررسی",
  hall_status_published: "منتشر شده",
  hall_status_suspended: "معلق",
  halls_capacity: "ظرفیت {total} نفر",
  halls_price_from: "· فی نفر از {price}",
  halls_rating: "★ {avg} ({count} نظر)",
  hall_edit: "ویرایش",
  hall_uploading: "در حال آپلود…",
  hall_add_photo: "عکس",
  hall_photos: "عکس‌ها",
  hall_add_menu: "منو",
  hall_submit_publish: "ارسال برای انتشار",
  hall_price_suggestion: "پیشنهاد قیمت",
  halls_empty: "هنوز تالاری ثبت نکرده‌اید — با دکمه «ثبت تالار» شروع کنید.",
  halls_toast_photo_too_big: "عکس باید کمتر از ۱۵ مگابایت باشد",
  halls_toast_photo_added: "عکس اضافه شد",
  halls_toast_photo_failed: "خطا در آپلود عکس",
  halls_toast_sent_publish: "برای بررسی و انتشار فرستاده شد",
  halls_toast_hall_created:
    "تالار ثبت شد — حالا منوهایش را اضافه کنید و بعد «ارسال برای انتشار» را بزنید",
  halls_toast_hall_saved: "تغییرات تالار ذخیره شد",
  halls_toast_menu_created: "منو ثبت شد",
  halls_price_dialog_title: "پیشنهاد قیمت — {hall}",
  halls_price_occupancy: "اشغال ۹۰ روز آینده: {pct}٪",
  halls_price_peak: "· پرتقاضاترین روز: {day}",
  halls_price_col_menu: "منو",
  halls_price_col_current: "قیمت فعلی (فی نفر)",
  halls_price_col_suggested: "پیشنهاد",
  halls_price_footnote: "پیشنهادها مشورتی‌اند — تغییر قیمت همیشه با تأیید شماست.",

  // hall types
  hall_type_luxury: "لوکس",
  hall_type_standard: "استندرد",
  hall_type_garden: "باغ",
  hall_type_vip_salon: "سالون VIP",

  // amenities
  amenity_parking: "پارکینگ",
  amenity_generator: "جنراتور",
  amenity_bridal_room: "اتاق عروس",
  amenity_vip_room: "اتاق VIP",
  amenity_av_system: "سیستم صوتی",
  amenity_garden: "باغ",

  // hall form
  hallform_edit_title: "ویرایش تالار",
  hallform_create_title: "ثبت تالار جدید",
  hallform_name: "نام تالار",
  hallform_city: "شهر",
  hallform_type: "نوع تالار",
  hallform_cap_total: "ظرفیت کل",
  hallform_cap_mens: "مردانه",
  hallform_cap_womens: "زنانه",
  hallform_hall_fee: "کرایه تالار (افغانی، جدا از فی نفر)",
  hallform_deposit_pct: "بیعانه (٪ از مجموع)",
  hallform_instant: "رزرو فوری (بدون نیاز به تأیید شما، مستقیم به مرحله بیعانه می‌رود)",
  hallform_amenities: "امکانات",
  hallform_cancel_policy:
    "شرایط لغو پیش‌فرض: تا ۳۰ روز قبل ۱۰۰٪، تا ۷ روز قبل ۵۰٪ بازپرداخت بیعانه — بعداً قابل ویرایش است.",
  hallform_save_changes: "ذخیره تغییرات",
  hallform_create_submit: "ثبت تالار",
  hallform_error: "خطا در ذخیره تالار",

  // menu form
  menuform_title: "منوی جدید (قیمت فی نفر)",
  menuform_name: "نام منو",
  menuform_name_placeholder: "مثلاً: منوی عادی",
  menuform_price: "قیمت فی نفر (افغانی)",
  menuform_courses: "غذاها (با ، جدا کنید)",
  menuform_courses_placeholder: "قابلی، کباب، سلاته، نوشابه",
  menuform_submit: "ثبت منو",
  menuform_error: "خطا در ثبت منو",

  // shared (more)
  common_delete: "حذف",
  common_reject: "رد",
  common_publish: "انتشار",

  // hall photos dialog
  photos_load_failed: "عکس‌ها بارگذاری نشد",
  photos_delete_failed: "حذف عکس ناموفق بود",
  photos_title: "عکس‌های {hall}",
  photos_intro:
    "نخستین عکس، عکس اصلی تالار است و در نتایج جستجو دیده می‌شود. با حذف آن، عکس بعدی جایش را می‌گیرد.",
  photos_empty: "هنوز عکسی اضافه نشده — بدون عکس، تالار در نتایج جستجو جای خالی نشان می‌دهد.",
  photos_cover_chip: "عکس اصلی",
  photos_delete_aria: "حذف عکس",
  photos_add: "افزودن عکس",
  photos_confirm_title: "این عکس حذف شود؟",
  photos_confirm_cover: "این عکس اصلی تالار است. با حذف آن، عکس بعدی جایش را می‌گیرد.",
  photos_confirm_generic: "این کار برگشت‌پذیر نیست.",

  // printable receipt
  receipt_print_title: "رسید پرداخت",
  receipt_print_subtitle: "رسید {purpose}",
  receipt_print_no: "شماره رسید",
  receipt_print_issued: "تاریخ صدور",
  receipt_print_hall: "تالار",
  receipt_print_customer: "مشتری",
  receipt_print_phone: "شماره تماس",
  receipt_print_event_date: "تاریخ محفل",
  receipt_print_method: "طریقه پرداخت",
  receipt_print_amount: "مبلغ دریافتی",
  receipt_print_contract_total: "مجموع قرارداد",
  receipt_print_total_paid: "مجموع پرداخت‌شده",
  receipt_print_balance: "باقی‌مانده",
  receipt_print_note: "یادداشت",
  receipt_print_hall_sign: "امضای تالار: ____________",
  receipt_print_customer_sign: "امضای مشتری: ____________",
  receipt_print_footer: "این رسید از سیستم تالار صادر شده است.",

  // admin
  admin_kpi_published: "تالارهای منتشر شده",
  admin_kpi_awaiting: "در انتظار بررسی",
  admin_kpi_confirmed: "رزروهای تأیید شده",
  admin_kpi_tickets: "تیکت‌های باز",
  admin_pending_halls_title: "تالارهای در انتظار بررسی",
  admin_col_name: "نام",
  admin_col_city: "شهر",
  admin_col_type: "نوع",
  admin_col_capacity: "ظرفیت",
  admin_approve_publish: "تأیید و انتشار",
  admin_empty_pending: "موردی در انتظار بررسی نیست",
  admin_settlement_title: "تسویه پرداخت‌ها",
  admin_run_settlement: "اجرای تسویه",
  admin_running: "در حال اجرا…",
  admin_col_org: "سازمان",
  admin_col_gross: "ناخالص",
  admin_col_commission: "کمیسیون",
  admin_col_net: "خالص",
  admin_mark_paid: "ثبت پرداخت",
  admin_empty_payouts: "برای مشاهدهٔ پرداخت‌های تسویه‌نشده «اجرای تسویه» را بزنید",
  admin_reviews_title: "نظرات در انتظار تأیید",
  admin_col_hall: "تالار",
  admin_col_rating: "امتیاز",
  admin_col_text: "متن",
  admin_col_date: "تاریخ",
  admin_empty_reviews: "نظری در انتظار تأیید نیست",

  // sign-up / password reset / auth errors (LoginPage)
  login_no_account: "هنوز حساب ندارید؟",
  login_create_account: "ساخت حساب برای تالار",
  login_have_account: "حساب دارید؟ وارد شوید",
  login_forgot: "رمز را فراموش کرده‌اید؟",
  signup_title: "ساخت حساب برای تالار تان",
  signup_subtitle: "رایگان است. بعد از ساخت حساب، نام تالار یا شرکت تان را ثبت می‌کنید.",
  signup_name: "نام و تخلص",
  signup_password_repeat: "تکرار رمز عبور",
  signup_submit: "ساخت حساب",
  signup_submitting: "در حال ساخت حساب…",
  signup_password_short: "رمز عبور باید حداقل {n} حرف باشد",
  signup_password_mismatch: "رمز عبور و تکرار آن یکسان نیستند",
  forgot_need_email: "اول ایمیل خود را در بالا بنویسید",
  forgot_sent:
    "اگر این ایمیل در تالار حساب داشته باشد، لینک تغییر رمز به آن فرستاده شد. صندوق ایمیل (و Spam) را ببینید.",
  auth_err_invalid_credential: "ایمیل یا رمز عبور درست نیست",
  auth_err_email_in_use: "با این ایمیل قبلاً حساب ساخته شده — وارد شوید یا رمز را بازیابی کنید",
  auth_err_invalid_email: "ایمیل درست نوشته نشده",
  auth_err_weak: "رمز بسیار ساده است — حرف و رقم را با هم بیامیزید",
  auth_err_disabled: "این حساب غیرفعال شده — با پشتیبانی تالار تماس بگیرید",
  auth_err_generic: "ورود ناموفق بود — دوباره امتحان کنید",

  // calendar (Jalali month grid)
  calendar_today: "امروز",
  calendar_prev_month: "ماه قبل",
  calendar_next_month: "ماه بعد",
  calendar_legend_free: "خالی",
  calendar_legend_partial: "یک وقت رزرو شده",
  calendar_legend_full: "پُر",
  calendar_month_summary: "{count} رزرو در این ماه",
  weekday_sat: "شنبه",
  weekday_sun: "یک‌شنبه",
  weekday_mon: "دوشنبه",
  weekday_tue: "سه‌شنبه",
  weekday_wed: "چهارشنبه",
  weekday_thu: "پنج‌شنبه",
  weekday_fri: "جمعه",

  // dashboard — getting started (new hall owner)
  start_title: "شروع کار — تا اولین رزرو",
  start_step_hall: "تالار خود را ثبت کنید: نام، شهر، ظرفیت و کرایه",
  start_step_menu: "منو و قیمت فی نفر را اضافه کنید، و عکس بگذارید",
  start_step_publish:
    "برای انتشار بفرستید — بعد از تأیید، مشتریان تالار تان را در اپ می‌بینند و رزرو می‌کنند",
  start_pending: "تالار شما برای بررسی فرستاده شده — بعد از تأیید در اپ مشتریان دیده می‌شود.",
  start_go_halls: "رفتن به تالارها",

  // finance — account types
  acct_type_asset: "دارایی",
  acct_type_liability: "بدهی",
  acct_type_equity: "سرمایه",
  acct_type_income: "عاید",
  acct_type_expense: "مصرف",

  // confirmations + accessible labels (UI/UX pass)
  common_confirm: "تأیید",
  cp_show_password: "نمایش رمز",
  cp_hide_password: "پنهان کردن رمز",
  confirm_ledger_note: "ثبت در دفاتر قابل ویرایش نیست؛ اصلاح فقط با سند معکوس ممکن است.",
  confirm_verify_title: "رسید در دفاتر ثبت شود؟",
  confirm_verify_body: "رسید {no} به مبلغ {amount} تأیید و در دفاتر ثبت می‌شود.",
  confirm_payroll_title: "معاش پرداخت و ثبت شود؟",
  confirm_payroll_body: "لیست معاش {period} به مجموع {total} پرداخت‌شده ثبت و در دفاتر وارد می‌شود.",
  confirm_paid_title: "این تسویه پرداخت‌شده ثبت شود؟",
  confirm_paid_body: "تسویهٔ {amount} برای سازمان {org} پرداخت‌شده علامت می‌خورد. فقط وقتی تأیید کنید که پول واقعاً انتقال یافته است.",
  confirm_settle_title: "تسویه اجرا شود؟",
  confirm_settle_body: "برای همهٔ پرداخت‌های آنلاینِ تأییدشده که هنوز تسویه نشده‌اند، تسویه ساخته می‌شود.",
  confirm_reject_hall_title: "تالار رد شود؟",
  confirm_reject_hall_body: "«{name}» منتشر نمی‌شود.",
  confirm_reject_review_title: "نظر رد شود؟",
  confirm_reject_review_body: "این نظر منتشر نمی‌شود.",
} as const;

export type StringKey = keyof typeof fa;
